/**
 * @packageDocumentation
 * @module f1/database
 */

import { t_lens, t_pointer } from '../types';
import is_string from '../is/is_string';
import is_array from '../is/is_array';

/** @ignore match escape characters */
const mEsc = /~[01]/g;
/** @ignore has escape characters */
const hEsc = /~/;

/** @ignore convert external lens forms to internal forms */
function replaceEsc(m: string) {
  switch (m) {
    case '~1':
      return '/';
    case '~0':
      return '~';
    default:
      return '';
  }
}

/** @ignore apply the escape characters */
function unTilde(str: string) {
  return hEsc.test(str) ? str.replace(mEsc, replaceEsc) : str;
}

export interface Lens {
  /**
   * Convert a pointer (e.g. `/p1/p2/p3`) to a lens `['', 'p1', 'p2', 'p3']` to use with `read` and `write`. When you create a lens you should
   * adhere to the following rules:
   * - Always start with a `/`
   * - Never end with a `/`
   *
   * Then you can create variables of references and append them safely
   * `/a/b` + `/c` => `/a/b/c`
   * ...which is valid and append without worrying about including a slash in between
   *
   * @public
   * @see f1.read uses a lens to read from an object
   * @see f1.write uses a lens to write to an object
   * @function f1.lens
   * @param pointer a lens or pointer to unwrap
   * @example
   *
   * ```ts
   * f1.lens('/p1') === ['', 'p1']
   * // - can be used to append to the last value
   * // - or read nothing because the last spot is always empty for read
   * f1.lens('/p1/-') === ['', 'p1', '-']
   * // read or write to an array index
   * f1.lens('/p1/1') === ['', 'p1', '1']
   * // invalid, start with / always
   * f1.lens('a/b') === ['a', 'b']
   * ```
   */
  (pointer: t_pointer | t_lens): t_lens;
}

/**
 * @see Lens
 */
export default (pointer: t_pointer | t_lens): t_lens => {
  const lens: string[] = is_string(pointer) ? pointer.split('/') : is_array(pointer) ? pointer : [];

  // @todo identify a poorly crafted json pointer (must start with /)
  // lens = pointer[0] === '' ? pointer : [];
  return lens.map(unTilde);
};
